// Polar API Configuration
interface PolarConfig {
  apiBaseUrl: string;
  eventTypes: {
    subscriptionCreated: string;
    subscriptionUpdated: string;
    subscriptionCanceled: string;
    paymentSucceeded: string;
    paymentFailed: string;
  };
  subscriptionStatus: {
    active: string;
    trialing: string;
    pastDue: string;
    canceled: string;
    unpaid: string;
  };
  tiers: {
    monthly: string;
    yearly: string;
    freeTrial: string;
    free: string;
  };
  defaultSuccessUrl: string;
  defaultCancelUrl: string;
  apiKey: string;
}

export const polarConfig: PolarConfig = {
  // Polar API base URL
  apiBaseUrl: 'https://api.polar.sh/api/v1',
  
  // Webhook event types
  eventTypes: {
    subscriptionCreated: 'subscription.created',
    subscriptionUpdated: 'subscription.updated',
    subscriptionCanceled: 'subscription.canceled',
    paymentSucceeded: 'payment.succeeded',
    paymentFailed: 'payment.failed',
  },
  
  // Subscription status mapping
  subscriptionStatus: {
    active: 'active',
    trialing: 'trialing',
    pastDue: 'past_due',
    canceled: 'canceled',
    unpaid: 'unpaid',
  },
  
  // Subscription tiers
  tiers: {
    monthly: 'monthly_pro',
    yearly: 'yearly_pro',
    freeTrial: 'free_trial',
    free: 'free',
  },
  
  // Default URLs
  defaultSuccessUrl: '/app/dashboard?checkout=success',
  defaultCancelUrl: '/app/dashboard?checkout=canceled',
  
  // API Key
  apiKey: process.env.NEXT_PUBLIC_POLAR_API_KEY || '',
};

// Hardcoded price IDs for development and testing
const HARDCODED_PRICE_IDS: {
  monthly_pro: string;
  yearly_pro: string;
} = {
  monthly_pro: 'price_monthly_pro', // Default value for development
  yearly_pro: 'price_yearly_pro'    // Default value for development
};

// In production, these should be set as environment variables
if (typeof window === 'undefined') {
  // Server-side
  HARDCODED_PRICE_IDS.monthly_pro = process.env.NEXT_PUBLIC_POLAR_MONTHLY_PRO_PRICE_ID || HARDCODED_PRICE_IDS.monthly_pro;
  HARDCODED_PRICE_IDS.yearly_pro = process.env.NEXT_PUBLIC_POLAR_YEARLY_PRO_PRICE_ID || HARDCODED_PRICE_IDS.yearly_pro;
}

// Get the Polar price ID based on the plan type
export function getPolarPriceId(planType: 'monthly_pro' | 'yearly_pro'): string {
  // Always provide a safe fallback to avoid runtime errors on client
  const fallback = planType === 'monthly_pro' ? 'price_monthly_pro' : 'price_yearly_pro';
  const priceId = HARDCODED_PRICE_IDS[planType] || fallback;
  console.log(`Using ${planType} price ID:`, priceId);
  return priceId;
}

// Helper function to build the checkout URL
export function buildCheckoutUrl(priceId: string, successUrl: string, cancelUrl: string): string {
  return `${polarConfig.apiBaseUrl}/subscriptions/create_checkout?price_id=${encodeURIComponent(priceId)}&success_url=${encodeURIComponent(successUrl)}&cancel_url=${encodeURIComponent(cancelUrl)}`;
}

// Helper function to get subscription status from Polar status
export function mapPolarStatusToSubscriptionStatus(polarStatus: string): string {
  switch (polarStatus) {
    case 'active':
      return polarConfig.subscriptionStatus.active;
    case 'trialing':
      return polarConfig.subscriptionStatus.trialing;
    case 'past_due':
      return polarConfig.subscriptionStatus.pastDue;
    case 'canceled':
    case 'unpaid':
      return polarConfig.subscriptionStatus.canceled;
    default:
      return polarConfig.subscriptionStatus.canceled;
  }
}
